import * as THREE from "three";
import { CameraTouch } from "./cameraMotion";
import { CameraShake, Trauma } from "./cameraShake";
import { CameraFollow } from "./cameraFollow";
import { type Follow, frameTarget, nextFollow, stepFollow } from "./cameraFraming";
import { CameraCore } from "./cameraCore";

export { Trauma } from "./cameraShake";

/**
 * Pure camera maths for the fixed 35° tilt / 45° yaw isometric view (DESIGN §12): frames a grid in portrait,
 * pinch zoom, 90° yaw snapping, drag pan clamped to the map edge. No renderer, so it is unit-testable in Node.
 *
 * Zoom is a multiplier on the distance that fits the whole grid: 1 shows the whole map, smaller is closer.
 * The look point (ground under the screen centre) is clamped so the island's edge stops near the screen edge:
 * a drag can never scroll off into the water beyond the map.
 *
 * The view's state, the insets and the projections are its base, cameraCore.ts (pure maths in cameraProject.ts); this
 * class adds the touch controls, the whole-map view, the shake (cameraShake.ts) and the placement follow
 * (cameraFraming.ts).
 */
export class IsoCamera extends CameraCore {
  private rotateAcc = 0;

  frame(gridWidth: number, gridHeight: number): void {
    this.touch.stop();
    this.beforeWhole = null;
    this.centre.set((gridWidth - 1) / 2, 0, (gridHeight - 1) / 2);
    this.halfW = gridWidth / 2 + 0.5;
    this.halfH = gridHeight / 2 + 0.5;
    this.pan.set(0, 0, 0);
    this.zoom = IsoCamera.DefaultZoom;
    this.zoomLimits = null; this.lookClamp = null;          // a screen's own range (the hub's pinch) ends with it
    this.refit();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Touch feel (QoL C1–C3, render/cameraMotion.ts): eased pinch about the fingers, flick momentum, the whole-map view
  // -------------------------------------------------------------------------------------------------------------------

  private readonly touch: CameraTouch = this.makeTouch();
  private makeTouch(): CameraTouch {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const cam = this;
    return new CameraTouch({
    get zoom(): number { return cam.zoom; },
    setZoom: (z) => { const r = this.zoomRange; this.zoom = Math.min(r.max, Math.max(r.min, z)); this.apply(); },
    groundAt: (px, py) => this.viewportToGround(px, py),
    panBy: (dx, dz) => {
      const was = this.pan.clone();
      this.pan.x += dx; this.pan.z += dz;
      this.apply();
      return { x: this.pan.x - was.x, z: this.pan.z - was.z };
    },
    get probe() { return { x: cam.width / 2, y: (cam.insetEase.framedTop + cam.height - cam.insetEase.framedBottom) / 2 }; },
    });
  }

  /**
   * A finger pinch (viewport px): eases to the fingers' zoom about the point between them, so the ground under the
   * fingers stays under them (QoL C1). `onPinch` (no anchor, at once) stays for the tests and scripted zooms.
   */
  pinchAt(scale: number, px: number, py: number): void {
    this.follow = null; this.glideTo = null;
    this.beforeWhole = null;                                 // a pinch in hand ends the whole-map view
    const r = this.zoomRange;
    this.touch.pinch(scale, px, py, r.min, r.max);
  }

  /** A flicked pan released at (vx, vy) viewport px/s carries on and slows (QoL C2), clamped at the map edge. */
  fling(vx: number, vy: number): void { this.touch.fling(vx, vy); }

  /** Whether the whole-map view is up (QoL C3), and the view it will return to. */
  get wholeMap(): boolean { return this.beforeWhole !== null; }
  private beforeWhole: { x: number; z: number; zoom: number } | null = null;

  /**
   * The whole-map view (QoL C3: a double-tap or the map button): eases out to the zoom that fits the map, centred.
   * Called again it eases back in, to the view it left or centred on `at` (the double-tapped spot).
   */
  toggleWholeMap(at: { x: number; z: number } | null = null): void {
    if (this.beforeWhole !== null) {
      const back = this.beforeWhole;
      this.beforeWhole = null;
      this.followTo({ x: at?.x ?? back.x, z: at?.z ?? back.z, zoom: back.zoom }, IsoCamera.WholeMapSmoothSec);
      return;
    }
    const look = this.lookPoint();
    this.beforeWhole = { x: look.x, z: look.z, zoom: Math.min(this.zoom, IsoCamera.WholeMapReturnZoom) };
    this.followTo({ x: this.centre.x, z: this.centre.z, zoom: IsoCamera.MaxZoom }, IsoCamera.WholeMapSmoothSec);
  }
  /** The whole-map ease's spring time (s): a touch slower than a placement glide, so the zoom-out reads as a move. */
  static readonly WholeMapSmoothSec = 0.16;
  /** Coming back from the whole map never lands further out than this (a whole-map toggle from far out lands close). */
  static readonly WholeMapReturnZoom = IsoCamera.DefaultZoom * 1.5;

  /** The view as it stands (look point, zoom, yaw), to rebuild a level under the same view (undo, D17). */
  viewState(): { x: number; z: number; zoom: number; yaw: number } {
    const look = this.lookPoint();
    return { x: look.x, z: look.z, zoom: this.zoom, yaw: this.targetYaw };
  }
  restoreView(s: { x: number; z: number; zoom: number; yaw: number }): void {
    this.touch.stop();
    this.yaw = this.targetYaw = s.yaw;
    const r = this.zoomRange;
    this.zoom = Math.min(r.max, Math.max(r.min, s.zoom));
    this.focus(s.x, s.z);
  }

  /**
   * Degrees of twist within one touch for the first 90° snap. It was 45 — with the gesture's own lock-in taking the
   * first 8–12° of it, a phone twist had to reach 45° in one go, which a hand turning two fingertips rarely does
   * (Kevin, 2026-09-23: "rotating the map has broken completely"). A deliberate 20–30° twist now turns the map.
   */
  static readonly RotateSnapDeg = 18;
  /** Further twist in the same touch for each further snap, so a generous 30–40° twist is still one quarter turn. */
  static readonly RotateRepeatDeg = 45;
  private snapsThisTurn = 0;

  /**
   * A two-finger touch began or lifted: the partial twist is dropped, so several small twists cannot add up to a
   * surprise 90°, and the next turn needs only the first-snap twist again.
   */
  rotateEnd(): void { this.rotateAcc = 0; this.snapsThisTurn = 0; }
  /** Twist in degrees (a rotate button passes ±45, which is always a full step whatever came before it). */
  onRotate(deg: number): void {
    this.rotateAcc += deg;
    const need = this.snapsThisTurn === 0 ? IsoCamera.RotateSnapDeg : IsoCamera.RotateRepeatDeg;
    if (Math.abs(this.rotateAcc) >= need) { this.targetYaw += this.rotateAcc > 0 ? 90 : -90; this.rotateAcc = 0; this.snapsThisTurn++; }
  }

  private dragAnchor: THREE.Vector3 | null = null;

  /** Drag pan: the ground point under the finger at drag start stays under the finger, within the map edge. */
  dragBegin(px: number, py: number): void { this.touch.stop(); this.dragAnchor = this.groundAt(px, py); }

  dragTo(px: number, py: number): void {
    if (this.dragAnchor === null) return;
    const now = this.groundAt(px, py);
    if (now === null) return;
    this.pan.add(this.dragAnchor.clone().sub(now));
    this.pan.y = 0;
    this.apply();
  }

  dragEnd(): void { this.dragAnchor = null; }
  /** Moves the look point to a ground position (clamped like a drag), e.g. to open on the base. */
  focus(x: number, z: number): void {
    this.touch.stop();
    this.pan.set(x - this.centre.x, 0, z - this.centre.z);
    this.apply();
  }

  private readonly shaker = new CameraShake();
  private glideTo: THREE.Vector3 | null = null;

  /** The old boss-arrival shake (P1.8.4): what `shake` does by default, and the unit its kicks are measured in. */
  static readonly ShakeSeconds = 0.5;
  static readonly ShakeStrength = 0.25;

  /** Adds camera trauma (0..1, clamped); see `Trauma`. Callers skip it under reduced motion. */
  addTrauma(amount: number): void { this.shaker.add(amount); }
  get currentTrauma(): number { return this.shaker.current; }

  /**
   * A shake of the old (seconds, strength) kind — the boss arrival (P1.8.4) at the defaults, the wave's kicks
   * (waveFx.ts) smaller — now a trauma kick. The old jitter's peak was seconds × strength and the trauma shake is
   * trauma², so the defaults give the boss-spawn budget and a smaller kick the square root of its share of it.
   * Callers skip it under reduced motion.
   */
  shake(seconds = IsoCamera.ShakeSeconds, strength = IsoCamera.ShakeStrength): void {
    const share = Math.max(0, seconds * strength) / (IsoCamera.ShakeSeconds * IsoCamera.ShakeStrength);
    this.addTrauma(Trauma.Budget.bossSpawn * Math.sqrt(share));
  }

  /** Tap-to-focus: glides the look point to a ground position over a few frames. */
  glide(x: number, z: number): void { this.touch.stop(); this.follow = null; this.glideTo = new THREE.Vector3(x - this.centre.x, 0, z - this.centre.z); }

  update(dt: number): boolean {
    let moved = this.insetEase.step(dt);
    if (this.stepFollow(dt)) moved = true;                 // it re-applies the framing itself
    else if (moved) this.apply();
    if (this.touch.step(dt)) moved = true;
    if (this.glideTo !== null) {
      const d = this.glideTo.clone().sub(this.pan);
      if (d.length() < 0.02) { this.pan.copy(this.glideTo); this.glideTo = null; }
      else this.pan.add(d.multiplyScalar(Math.min(1, dt * 8)));
      this.pan.y = 0;
      this.apply();
      moved = true;
    }
    if (Math.abs(this.yaw - this.targetYaw) > 0.01) {
      const step = 360 * dt;
      const d = this.targetYaw - this.yaw;
      this.yaw += Math.abs(d) <= step ? d : Math.sign(d) * step;
      this.apply();
      moved = true;
    }
    if (this.shaker.active) {
      this.shaker.step(dt, this.camera, this.fitDistance * this.zoom, () => this.apply());
      moved = true;
    }
    return moved;
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Placement follow: the view eases to what the player is placing (cameraFollow.ts has the spring and the fit rules)
  // -------------------------------------------------------------------------------------------------------------------

  private follow: Follow | null = null;

  /** Whether a placement glide is under way. */
  get following(): boolean { return this.follow !== null; }

  /**
   * Where the look point and zoom must go for `points` (world positions) to sit in the band between the insets, centred
   * or (`centre` false) moved only as far as it takes; the camera is left exactly where it was (cameraFraming.ts).
   */
  frameTarget(points: readonly { x: number; y: number; z: number }[], centre: boolean, fromZoom = this.zoom): { x: number; z: number; zoom: number } {
    return frameTarget(this.framing, points, centre, fromZoom);
  }

  /**
   * Eases the look point and zoom to `target` over the spring's `smoothTime` (seconds), from wherever the view is and
   * however it is already moving. A target inside the dead zone of where the view already is does nothing, so the
   * camera never twitches for a cell's worth of change.
   */
  followTo(target: { x: number; z: number; zoom: number }, smoothTime = CameraFollow.SmoothTime): void {
    const f = nextFollow(this.framing, target, smoothTime, this.follow);
    if (f === null) return;
    this.follow = f;
    this.glideTo = null;                                    // one mover at a time: the newer request wins
    this.touch.stop();
  }

  /** The player took the camera (a drag, a pinch, a twist): any glide stops where it is, at once. */
  cancelFollow(): void { this.follow = null; this.glideTo = null; }

  private stepFollow(dt: number): boolean {
    const f = this.follow;
    if (f === null) return false;
    if (stepFollow(this.framing, f, dt)) this.follow = null;
    return true;
  }
}
